/* Apply Illumikids quantity breaks to every board in the cart. */
(() => {
  if (window.IllumikidsPackCart) return;

  const PRODUCT = 8720958947428;
  const SMALL = 49162563780708;
  const LARGE = 49162563813476;
  const GIFT = 49158346834020;
  const DISCOUNT = '_illumikids_discount';
  const PACK = '_illumikids_pack_id';
  const root = window.Shopify?.routes?.root || '/';
  let tail = Promise.resolve();
  let lineBusy = false;

  const board = item => Number(item.product_id) === PRODUCT &&
    (Number(item.variant_id) === SMALL || Number(item.variant_id) === LARGE);
  const boardVariant = id => Number(id) === SMALL || Number(id) === LARGE;
  const endpoint = path => `${root}${path === 'cart.js' ? path : `cart/${path}`}`;
  const sameProperties = (a, b) => JSON.stringify(Object.entries(a || {}).sort()) ===
    JSON.stringify(Object.entries(b || {}).sort());

  function planCart(cart) {
    const lines = cart.items.filter(board);
    const count = lines.reduce((sum, line) => sum + line.quantity, 0);
    const tier = count >= 3 ? 'trio' : count === 2 ? 'duo' : null;
    const assignments = new Map();
    for (const line of lines) {
      const properties = { ...(line.properties || {}) };
      delete properties[DISCOUNT];
      delete properties[PACK];
      if (tier) properties[DISCOUNT] = `${tier}_${Number(line.variant_id) === SMALL ? 'small' : 'large'}`;
      assignments.set(line, [{ quantity: line.quantity, properties }]);
    }
    return assignments;
  }

  async function request(path, body) {
    const response = await window.fetch(endpoint(path), {
      method: body ? 'POST' : 'GET',
      illumikidsSkipGiftGuard: true,
      headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
      ...(body ? { body: JSON.stringify(body) } : { cache: 'no-store' })
    });
    let data;
    try { data = await response.json(); } catch (_) { throw new Error('Impossible de mettre à jour le panier. Veuillez réessayer.'); }
    if (!response.ok || data.status >= 400 || data.errors) {
      throw new Error(data.description || (typeof data.errors === 'string' ? data.errors : '') ||
        'Impossible de mettre à jour le panier. Veuillez réessayer.');
    }
    return data;
  }

  async function acquire() {
    const previous = tail;
    let release;
    tail = new Promise(resolve => { release = resolve; });
    await previous;
    return release;
  }

  const sellingPlanId = line => line.selling_plan_allocation?.selling_plan?.id || line.selling_plan || null;
  const sameSellingPlan = (a, b) => String(sellingPlanId(a) || '') === String(sellingPlanId(b) || '');

  // Shopify ignores properties: {} on change.js once properties were set.
  async function clearAllProperties(cart, line) {
    const originalCount = cart.items.filter(board).reduce((sum, item) => sum + item.quantity, 0);
    const withoutProperties = item => board(item) &&
      Number(item.variant_id) === Number(line.variant_id) && sameSellingPlan(item, line) &&
      !Object.keys(item.properties || {}).length;
    const oldProperties = item => board(item) &&
      Number(item.variant_id) === Number(line.variant_id) && sameSellingPlan(item, line) &&
      sameProperties(item.properties, line.properties);
    const previousQuantity = cart.items.filter(withoutProperties)
      .reduce((sum, item) => sum + item.quantity, 0);
    const sellingPlan = sellingPlanId(line);
    await request('add.js', { items: [{
      id: Number(line.variant_id), quantity: line.quantity,
      ...(sellingPlan ? { selling_plan: sellingPlan } : {})
    }] });
    try {
      const afterAdd = await request('cart.js');
      const old = afterAdd.items.find(oldProperties);
      if (!old || old.quantity !== line.quantity) throw new Error('Le panier a changé. Rechargez la page.');
      return await request('change.js', { id: old.key, quantity: 0 });
    } catch (error) {
      try {
        const current = await request('cart.js');
        if (!current.items.some(oldProperties) &&
          current.items.filter(board).reduce((sum, item) => sum + item.quantity, 0) === originalCount) {
          return current;
        }
        const added = current.items.find(withoutProperties);
        if (added?.quantity === previousQuantity + line.quantity) {
          await request('change.js', { id: added.key, quantity: previousQuantity });
        }
      } catch (_) { /* Keep the original error. */ }
      throw error;
    }
  }

  // Caller owns the lock. Shopify changes line keys when properties change.
  async function reconcile(initialCart) {
    let cart = initialCart || await request('cart.js');
    let changed = false;
    const boardCount = () => cart.items.filter(board).reduce((sum, item) => sum + item.quantity, 0);
    const originalCount = boardCount();
    const maxPasses = Math.max(12, originalCount * 3);
    for (let pass = 0; pass < maxPasses; pass++) {
      const assignments = planCart(cart);
      const pending = [...assignments].filter(([line, segments]) =>
        segments.length !== 1 || segments[0].quantity !== line.quantity ||
        !sameProperties(segments[0].properties, line.properties));
      const work = pending.find(([, segments]) => segments.length === 1) || pending[0];
      if (!work) {
        // Keep at most one gift while a board remains; remove every orphan gift.
        const gifts = cart.items.filter(item => Number(item.variant_id) === GIFT);
        let keep = originalCount > 0 ? 1 : 0;
        for (const gift of gifts) {
          const wanted = keep;
          keep = 0;
          if (gift.quantity !== wanted) {
            cart = await request('change.js', { id: gift.key, quantity: wanted });
            changed = true;
          }
        }
        cart.illumikids_pack_changed = changed;
        return cart;
      }
      const [line, segments] = work;
      const [primary, ...extras] = segments;
      if (!extras.length && !Object.keys(primary.properties).length && Object.keys(line.properties || {}).length) {
        cart = await clearAllProperties(cart, line);
        changed = true;
        if (boardCount() !== originalCount) throw new Error('La quantité a changé. Rechargez la page.');
        continue;
      }
      cart = await request('change.js', {
        id: line.key, quantity: primary.quantity, properties: primary.properties
      });
      changed = true;
      try {
        if (extras.length) {
          const sellingPlan = line.selling_plan_allocation?.selling_plan?.id || line.selling_plan;
          const items = extras.map(extra => ({
            id: Number(line.variant_id), quantity: extra.quantity, properties: extra.properties,
            ...(sellingPlan ? { selling_plan: sellingPlan } : {})
          }));
          await request('add.js', { items });
        }
      } catch (error) {
        const current = await request('cart.js');
        const modified = current.items.find(item => board(item) &&
          Number(item.variant_id) === Number(line.variant_id) &&
          sameProperties(item.properties, primary.properties));
        if (modified) await request('change.js', {
          id: modified.key, quantity: line.quantity, properties: line.properties || {}
        });
        throw error;
      }
      if (extras.length) cart = await request('cart.js');
      if (boardCount() !== originalCount) throw new Error('La quantité a changé. Rechargez la page.');
    }
    throw new Error('Impossible de terminer la mise à jour du panier. Veuillez réessayer.');
  }

  async function sectionHTML(ids) {
    const url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    const response = await window.fetch(url.pathname + url.search, { cache: 'no-store' });
    if (!response.ok) throw new Error('Impossible de rafraîchir le panier. Rechargez la page.');
    const html = await response.json();
    if (!ids.every(id => html[id])) throw new Error('Impossible de rafraîchir le panier. Rechargez la page.');
    return html;
  }

  function replaceSections(owner, specs, html) {
    if (typeof owner.getSectionInnerHTML !== 'function') return false;
    for (const spec of specs) {
      const section = document.getElementById(spec.id);
      const target = spec.selector
        ? section?.querySelector(spec.selector) || document.querySelector(spec.selector)
        : section;
      if (!target) return false;
      target.innerHTML = owner.getSectionInnerHTML(html[spec.section || spec.id], spec.selector);
    }
    return true;
  }

  async function refresh(cart, options = {}) {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.renderContents === 'function') {
      const specs = drawer.getSectionsToRender();
      const ids = specs.map(section => section.id);
      const html = ids.every(id => cart.sections?.[id]) ? cart.sections : await sectionHTML(ids);
      if (options.silent || document.querySelector('cart-items')) {
        if (!replaceSections(drawer, specs, html)) window.location.reload();
      } else drawer.renderContents({ ...cart, sections: html });
    }
    const page = document.querySelector('cart-items');
    if (page) {
      const specs = page.getSectionsToRender?.();
      if (!specs?.length || !replaceSections(page, specs,
        await sectionHTML(specs.map(section => section.section || section.id)))) {
        window.location.reload();
      } else page.classList.toggle('is-empty', cart.item_count === 0);
    }
  }

  function errorMessage(error) {
    let alert = document.getElementById('illumikids-pack-cart-error');
    if (!alert) {
      alert = document.createElement('p');
      alert.id = 'illumikids-pack-cart-error';
      alert.setAttribute('role', 'alert');
      alert.style.cssText = 'color:#a32323;font-size:13px;margin:10px 16px';
      (document.querySelector('cart-drawer .drawer__contents') ||
        document.querySelector('cart-items') || document.body).prepend(alert);
    }
    alert.textContent = error?.message || 'Impossible de mettre à jour le panier. Veuillez réessayer.';
  }

  function lineFor(element) {
    const row = element.closest('.cart-item');
    const input = row?.querySelector('.quantity__input[data-index]');
    if (!input || !boardVariant(input.dataset.quantityVariantId)) return null;
    const key = row.dataset.lineKey || input.name.match(/^updates\[(.*)\]$/)?.[1];
    return key ? { key, row } : null;
  }

  async function changeLine(key, quantity, owner) {
    lineBusy = true;
    const wasInert = owner?.inert;
    if (owner) {
      owner.inert = true;
      owner.setAttribute('aria-busy', 'true');
    }
    const release = await acquire();
    let changed = false;
    try {
      const before = await request('cart.js');
      const item = before.items.find(line => line.key === key);
      if (!item || !board(item)) throw new Error('Cet article a changé. Rechargez la page et réessayez.');
      const fastBody = singleLineChange(before, item, quantity);
      const changedCart = await request('change.js', fastBody || { id: item.key, quantity });
      changed = true;
      await refresh(await reconcile(changedCart));
    } catch (error) {
      errorMessage(error);
      if (changed) window.location.reload();
      throw error;
    } finally {
      release();
      lineBusy = false;
      if (owner) {
        owner.inert = wasInert;
        owner.removeAttribute('aria-busy');
      }
    }
  }

  function singleLineChange(cart, item, quantity) {
    const boards = cart.items.filter(board);
    if (boards.length !== 1 || boards[0].key !== item.key || quantity < 1 || !document.querySelector('cart-drawer') ||
      document.querySelector('cart-items')) return null;
    const simulated = { items: [{ ...item, quantity }] };
    const [[, segments]] = [...planCart(simulated)];
    const properties = segments[0].properties;
    if (!Object.keys(properties).length && Object.keys(item.properties || {}).length) return null;
    const drawer = document.querySelector('cart-drawer');
    const sections = typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender().map(section => section.id) : [];
    return {
      id: item.key, quantity, properties,
      ...(sections.length ? { sections, sections_url: window.location.pathname } : {})
    };
  }

  document.addEventListener('change', event => {
    if (!event.target.matches?.('cart-items .quantity__input, cart-drawer-items .quantity__input')) return;
    const line = lineFor(event.target);
    if (!line) return;
    event.stopImmediatePropagation();
    if (lineBusy) return;
    const quantity = Number(event.target.value);
    if (!Number.isSafeInteger(quantity) || quantity < 0) {
      errorMessage(new Error('Saisissez une quantité valide.'));
      return;
    }
    changeLine(line.key, quantity, line.row.closest('cart-drawer, cart-items')).catch(() => {});
  }, true);

  document.addEventListener('click', event => {
    const quantityButton = event.target.closest?.('cart-items quantity-input button[name], cart-drawer-items quantity-input button[name]');
    if (quantityButton && ['plus', 'minus'].includes(quantityButton.name)) {
      const line = lineFor(quantityButton);
      if (line) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (lineBusy) return;
        const input = line.row.querySelector('.quantity__input[data-index]');
        const step = Number(input.step) || 1;
        const quantity = Math.max(0, Number(input.value) + (quantityButton.name === 'plus' ? step : -step));
        changeLine(line.key, quantity, line.row.closest('cart-drawer, cart-items')).catch(() => {});
        return;
      }
    }
    const remove = event.target.closest?.('cart-items cart-remove-button, cart-drawer-items cart-remove-button');
    if (remove) {
      const line = lineFor(remove);
      if (!line) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (lineBusy) return;
      changeLine(line.key, 0, line.row.closest('cart-drawer, cart-items')).catch(() => {});
      return;
    }
    const checkout = event.target.closest?.('button[name="checkout"], input[name="checkout"], a[href*="/checkout"]');
    if (!checkout) return;
    if (checkout.tagName === 'A' && !/\/checkout\/?$/.test(new URL(checkout.href).pathname)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutNow(checkout.href || `${root}checkout`);
  }, true);

  async function checkoutNow(destination) {
    const release = await acquire();
    try {
      await reconcile();
      window.location.assign(destination);
    } catch (error) {
      errorMessage(error);
    } finally { release(); }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches?.('form[action*="/cart"]')) return;
    if (event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutNow(`${root}checkout`);
  }, true);

  window.IllumikidsPackCart = { acquire, reconcile, refresh };
  document.addEventListener('DOMContentLoaded', async () => {
    if (!document.querySelector('cart-items, cart-drawer')) return;
    const release = await acquire();
    try {
      const cart = await reconcile();
      if (cart.illumikids_pack_changed) await refresh(cart, { silent: true });
    } catch (error) { errorMessage(error); }
    finally { release(); }
  }, { once: true });
})();

